import moment from 'moment-timezone';
import { ALL_ATTRIBUTION_PARAMS } from './constants';

export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatPhoneInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);

  if (!digits) {
    return '';
  }

  if (digits.length < 4) {
    return `(${digits}`;
  }

  if (digits.length < 7) {
    return `(${digits.slice(0, 3)})${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const formatSlotToISO = (dateStr: string, timeStr: string): string => {
  const trimmedTime = timeStr.split(' ')[0] || timeStr;
  const [hhStr, mmStr, ssStr] = trimmedTime.split(':');

  const y = parseInt(dateStr.slice(0, 4), 10);
  const m = parseInt(dateStr.slice(5, 7), 10) - 1;
  const d = parseInt(dateStr.slice(8, 10), 10);
  const hh = parseInt(hhStr || '0', 10);
  const mm = parseInt(mmStr || '0', 10);
  const ss = parseInt(ssStr || '0', 10);

  const date = new Date(Date.UTC(y, m, d, hh, mm, ss, 0));
  return date.toISOString();
};

// Format (e.g., "Mon, October 27 at 9:00 am")
export const formatSelectedSlot = (selectedSlotId: string): string => {
  if (!selectedSlotId) return '';

  const date = new Date(selectedSlotId);
  if (isNaN(date.getTime())) return '';

  const dayOfWeek = date.toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  });
  const month = date.toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  const day = date.toLocaleDateString('en-US', {
    day: 'numeric',
    timeZone: 'UTC',
  });
  const time = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    })
    .toLowerCase();

  return `${dayOfWeek}, ${month} ${day} at ${time}`;
};

export const titlise = (title: string): string => {
  if (!title) return '';
  return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
}

export const chooseOperatory = (operatories?: string[] | null): string | null => {
  if (!Array.isArray(operatories) || operatories.length === 0) return null;
  return operatories[0] ?? null;
};

export const stripHTML = (htmlString: string) => {
  return htmlString.replace(/<[^>]*>/g, '');
}

export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const PHONE_REGEX = /^\(\d{3}\)\d{3}-\d{4}$/;

export const MIN_DOB = "1920-01-01";

export const getYesterday = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
};

export const format24HourTo12Hour = (time24: string): string => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${minutes} ${ampm}`;
};

export const format12HourTo24Hour = (time12: string): string => {
  if (!time12) return '';

  // Handle formats like "3:30 PM", "03:30 PM", "3:30PM"
  const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return '';

  let [, hours, minutes, ampm] = match;
  let h = parseInt(hours);

  if (ampm.toUpperCase() === 'PM' && h < 12) {
    h += 12;
  } else if (ampm.toUpperCase() === 'AM' && h === 12) {
    h = 0;
  }

  return `${h.toString().padStart(2, '0')}:${minutes}`;
};

export interface TimeOption {
  value: string;
  label: string;
}

interface WorkingHour {
  day: string;
  open: string;
  hours: number;
}

export const generateTimeOptions = (
  workingHours?: WorkingHour[],
  dayName?: string,
  incrementMinutes = 15,
  selectedDate?: Date
): TimeOption[] => {
  if (!workingHours?.length || !dayName) return [];

  const schedule = workingHours.find(wh => wh.day === dayName);
  if (!schedule || schedule.open === 'Closed' || schedule.hours === 0) return [];

  // Parse start time from range e.g. "7:00 am - 8:00 pm"
  const startPart = schedule.open.split('-')[0].trim();
  const start24 = format12HourTo24Hour(startPart);
  if (!start24) return [];

  const [h, m] = start24.split(':').map(Number);
  const startMin = h * 60 + m;
  const endMin = startMin + schedule.hours * 60;

  // Calculate current time in minutes if selected date is today
  let currentMinCutoff = -1;
  if (selectedDate) {
    const now = new Date();
    const isToday =
      now.getFullYear() === selectedDate.getFullYear() &&
      now.getMonth() === selectedDate.getMonth() &&
      now.getDate() === selectedDate.getDate();

    if (isToday) {
      currentMinCutoff = now.getHours() * 60 + now.getMinutes();
    }
  }

  const options: TimeOption[] = [];
  for (let minTotal = startMin; minTotal <= endMin; minTotal += incrementMinutes) {
    // Skip if time is in the past for today
    if (minTotal <= currentMinCutoff) continue;

    const hh = Math.floor(minTotal / 60);
    const mm = minTotal % 60;
    const value = `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
    options.push({ value, label: format24HourTo12Hour(value) });
  }
  return options;
};

export const isPracticeClosed = (
  workingHours?: WorkingHour[],
  date?: Date
): boolean => {
  if (!workingHours?.length) return false;

  const targetDay = date ? date.toLocaleDateString('en-US', { weekday: 'long' }) : undefined;
  if (!targetDay) return false;

  const schedule = workingHours.find(wh => wh.day === targetDay);
  return !schedule || schedule.open === 'Closed' || schedule.hours === 0;
};

export const getSlotHHMM = (slot: string) => (slot || '').split(' ')[0] || '';

//TO DO: handle it on backend side
export const isSlotInFuture = (dateStr: string, slot: string, bufferMinutes: number = 0, timeZone?: string): boolean => {  
  const practiceNow = timeZone ? moment().tz(timeZone) : moment();
  practiceNow.add(bufferMinutes, 'minutes');

  const slotTime = getSlotHHMM(slot);
  const [hourStr, minuteStr] = slotTime.split(':');
  const hour = parseInt(hourStr || '0', 10);
  const minute = parseInt(minuteStr || '0', 10);

  const [year, month, day] = dateStr.split('-').map(n => parseInt(n, 10));
  const slotDate = moment.tz([year, month - 1, day, hour, minute], timeZone || moment.tz.guess());

  return slotDate.isAfter(practiceNow);
};

export const formatTimeSlot = (slot: string) => {
  const hhmm = getSlotHHMM(slot);
  const [hStr, mStr] = hhmm.split(':');
  const hour24 = parseInt(hStr || '0', 10);
  const minutes = parseInt(mStr || '0', 10);
  const isPM = hour24 >= 12;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const minutesPadded = String(minutes).padStart(2, '0');
  return `${hour12}:${minutesPadded} ${isPM ? 'pm' : 'am'}`;
};

export const getDayName = (date: Date) => {
  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

export const getDayNumber = (date: Date) => {
  return date.getDate();
};

export const extractAttributionParams = (): Record<string, string> => {
  const params = new URLSearchParams(window.location.search);
  const attributionParams: Record<string, string> = {};

  ALL_ATTRIBUTION_PARAMS.forEach(param => {
    const value = params.get(param);
    if (value) {
      attributionParams[param] = value;
    }
  });

  return attributionParams;
};